import { getStorage } from '@watchlist/shared';
import { ReviewDraft } from '../hooks/useReviewDraft';

/**
 * Draft Cleanup Utility
 *
 * Manages automatic cleanup of stale review drafts to prevent storage bloat.
 * Should be called on app startup or periodically.
 */

const STALE_DRAFT_THRESHOLD_DAYS = 7;
const DRAFT_KEY_PREFIX = 'review_draft_';

/**
 * Check if a draft is stale (older than 7 days)
 */
const isDraftStale = (draft: ReviewDraft): boolean => {
  try {
    const now = new Date();
    const updatedAt = new Date(draft.updatedAt);
    const ageInDays = (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24);
    return ageInDays > STALE_DRAFT_THRESHOLD_DAYS;
  } catch (error) {
    console.error('Error checking draft age:', error);
    // If we can't parse the date, consider it stale to be safe
    return true;
  }
};

/**
 * Clean up stale drafts for a specific user
 *
 * Scans all storage keys for review drafts belonging to the user
 * and deletes those older than 7 days.
 *
 * @param userId - The user ID to clean up drafts for
 * @returns Promise<number> - Number of drafts deleted
 */
export const cleanupStaleDrafts = async (userId: string): Promise<number> => {
  if (!userId) {
    console.warn('⚠️ cleanupStaleDrafts: No userId provided');
    return 0;
  }

  try {
    const storage = getStorage();
    let deletedCount = 0;

    // Get all storage keys
    // Note: getAllKeys() may not be available on all platforms
    // We'll use a fallback approach if needed
    let allKeys: readonly string[] = [];

    try {
      allKeys = await storage.getAllKeys();
    } catch (error) {
      console.warn('⚠️ getAllKeys() not supported, skipping cleanup:', error);
      return 0;
    }

    // Filter for user's review draft keys
    const userDraftKeyPrefix = `${DRAFT_KEY_PREFIX}${userId}_`;
    const userDraftKeys = allKeys.filter(key => key.startsWith(userDraftKeyPrefix));

    console.log(`🧹 Found ${userDraftKeys.length} review draft(s) for user ${userId}`);

    // Check each draft and delete if stale
    for (const key of userDraftKeys) {
      try {
        const draftJson = await storage.getItem(key);

        if (!draftJson) {
          // Key exists but no data - clean it up
          await storage.removeItem(key);
          deletedCount++;
          console.log(`🗑️ Removed empty draft key: ${key}`);
          continue;
        }

        const draft = JSON.parse(draftJson) as ReviewDraft;

        if (isDraftStale(draft)) {
          await storage.removeItem(key);
          deletedCount++;
          console.log(`🗑️ Removed stale draft: ${key} (updated: ${draft.updatedAt})`);
        }
      } catch (error) {
        console.error(`Error processing draft ${key}:`, error);
        // Continue to next draft even if one fails
      }
    }

    if (deletedCount > 0) {
      console.log(`✅ Cleaned up ${deletedCount} stale draft(s)`);
    } else {
      console.log(`✅ No stale drafts to clean up`);
    }

    return deletedCount;
  } catch (error) {
    console.error('❌ Error in cleanupStaleDrafts:', error);
    return 0;
  }
};

/**
 * Clean up all drafts for a specific movie (regardless of user)
 *
 * Useful when a movie is deleted or removed from the system.
 *
 * @param movieId - The movie ID to clean up drafts for
 * @returns Promise<number> - Number of drafts deleted
 */
export const cleanupDraftsForMovie = async (movieId: string): Promise<number> => {
  if (!movieId) {
    console.warn('⚠️ cleanupDraftsForMovie: No movieId provided');
    return 0;
  }

  try {
    const storage = getStorage();
    let deletedCount = 0;

    // Get all storage keys
    let allKeys: readonly string[] = [];

    try {
      allKeys = await storage.getAllKeys();
    } catch (error) {
      console.warn('⚠️ getAllKeys() not supported, skipping cleanup:', error);
      return 0;
    }

    // Filter for drafts with this movie ID
    // Keys are in format: review_draft_${userId}_${movieId}
    const movieDraftKeys = allKeys.filter(key =>
      key.startsWith(DRAFT_KEY_PREFIX) && key.endsWith(`_${movieId}`)
    );

    console.log(`🧹 Found ${movieDraftKeys.length} draft(s) for movie ${movieId}`);

    // Delete all drafts for this movie
    for (const key of movieDraftKeys) {
      try {
        await storage.removeItem(key);
        deletedCount++;
        console.log(`🗑️ Removed draft: ${key}`);
      } catch (error) {
        console.error(`Error removing draft ${key}:`, error);
      }
    }

    if (deletedCount > 0) {
      console.log(`✅ Cleaned up ${deletedCount} draft(s) for movie ${movieId}`);
    }

    return deletedCount;
  } catch (error) {
    console.error('❌ Error in cleanupDraftsForMovie:', error);
    return 0;
  }
};

/**
 * Get count of drafts for a user (for debugging/analytics)
 */
export const getDraftCount = async (userId: string): Promise<number> => {
  if (!userId) return 0;

  try {
    const storage = getStorage();
    const allKeys = await storage.getAllKeys();
    const userDraftKeyPrefix = `${DRAFT_KEY_PREFIX}${userId}_`;
    return allKeys.filter(key => key.startsWith(userDraftKeyPrefix)).length;
  } catch (error) {
    console.error('Error getting draft count:', error);
    return 0;
  }
};
